import { useCallback, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useChatContext } from "../context/ChatContext.tsx";

const PEGA_CHAT_HTML = "/pega-chat.html";
const PEGA_CHAT_ID = import.meta.env.VITE_PEGA_CHAT_ID;
const PEGA_CHAT_MESSAGE_SOURCE = "pega-chat";
const APP_FORM_MESSAGE_SOURCE = "app-form";
const CHAT_EVENTS = {
  IFRAME_READY: "chat-iframe-ready",
  INITIALIZE_CHAT: "initialize-chat",
  CHAT_INITIALIZED: "chat-initialized",
  WINDOW_MAXIMIZED: "chat-window-maximized",
  WINDOW_MINIMIZED: "chat-window-minimized",
  MAXIMIZE_WINDOW: "maximize-chat-window",
};

const overlayRootId = "pega-chat-overlay-root";

/*
Participants:
  - React host application
  - Bridge script embedded via `pega-chat.html`
  - Remote Pega chat widget

Event flow:
  1. The user clicks "Start a conversation now"; the host shows a loading state and mounts the iframe.
  2. The iframe emits `chat-iframe-ready` once it attaches to the DOM and is ready for configuration.
  3. The host responds with `initialize-chat-widget`, passing the chat id and any session data.
  4. The iframe emits `chat-widget-initialized` when the Pega widget finishes bootstrapping.
  5. The host sends `maximize-chat-window` to expand the widget once everything is ready.
  6. User actions inside the widget emit `chat-window-minimized` or `chat-window-maximized`, allowing the host to track state.

Reference table:
  | Direction      | Type                   | Payload            | Meaning
  | iframe -> app  | chat-iframe-ready      | —                  | Iframe mounted and waiting for init.
  | app -> iframe  | initialize-chat-widget | { chatId, … }      | Host provides session context.
  | iframe -> app  | chat-widget-initialized| —                  | Widget finished loading.
  | app -> iframe  | maximize-chat-window   | —                  | Host requests the widget to expand.
  | iframe -> app  | chat-window-minimized  | —                  | User collapsed the widget; session stays alive.
  | iframe -> app  | chat-window-maximized  | —                  | User reopened the widget.
*/
export default function PegaChatWidget() {
  const {
    isChatOpen,
    isChatReady,
    hasChatSessionStarted,
    markChatReady,
    syncChatOpenState,
  } = useChatContext();
  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  useEffect(
    () => () => {
      const root = document.getElementById(overlayRootId);
      root?.parentElement?.removeChild(root);
    },
    [],
  );

  const postMessageToChat = useCallback(
    (message: { source: string; type: string; session?: unknown }) => {
      const iframeWindow = iframeRef.current?.contentWindow;
      if (!iframeWindow) {
        return;
      }

      iframeWindow.postMessage(JSON.stringify(message), window.origin);
    },
    [],
  );

  useEffect(() => {
    if (isChatReady && isChatOpen) {
      readjustRootOverlay(true);
      postMessageToChat({
        source: APP_FORM_MESSAGE_SOURCE,
        type: CHAT_EVENTS.MAXIMIZE_WINDOW,
      });
    } else if (!isChatOpen) {
      readjustRootOverlay(false);
    }

    const handleMessage = (event: MessageEvent) => {
      if (typeof event.data !== "string") {
        return;
      }

      let parsed: { source?: string; type?: string; payload?: unknown };
      try {
        parsed = JSON.parse(event.data);
      } catch {
        return;
      }

      if (parsed?.source !== PEGA_CHAT_MESSAGE_SOURCE) {
        return;
      }

      switch (parsed.type) {
        case CHAT_EVENTS.IFRAME_READY:
          postMessageToChat({
            source: APP_FORM_MESSAGE_SOURCE,
            type: CHAT_EVENTS.INITIALIZE_CHAT,
            session: {
              chatId: PEGA_CHAT_ID,
            },
          });
          break;
        case CHAT_EVENTS.CHAT_INITIALIZED:
          postMessageToChat({
            source: APP_FORM_MESSAGE_SOURCE,
            type: CHAT_EVENTS.MAXIMIZE_WINDOW,
          });
          markChatReady();
          readjustRootOverlay(true);
          break;
        case CHAT_EVENTS.WINDOW_MAXIMIZED:
          syncChatOpenState(true);
          readjustRootOverlay(true);
          break;
        case CHAT_EVENTS.WINDOW_MINIMIZED:
          readjustRootOverlay(false);
          syncChatOpenState(false);
          break;
        default: {
          console.error("Chat message unsupported", parsed);
        }
      }
    };

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [
    isChatOpen,
    isChatReady,
    markChatReady,
    postMessageToChat,
    syncChatOpenState,
  ]);

  if (!isChatOpen && !hasChatSessionStarted) {
    return null;
  }

  return createPortal(
    <iframe
      title="CMC Self Service QA"
      src={PEGA_CHAT_HTML}
      className={
        isChatOpen ? "h-[600px] w-[420px] max-w-full" : "h-[100px] w-[80px]"
      }
      allow="clipboard-read; clipboard-write"
      sandbox="allow-scripts allow-same-origin allow-popups allow-forms allow-top-navigation-by-user-activation"
      ref={iframeRef}
    />,
    ensureOverlayRoot(),
  );
}

function ensureOverlayRoot(): HTMLElement {
  let root = document.getElementById(overlayRootId);

  if (!root) {
    root = document.createElement("div");
    root.style.position = "absolute";
    root.style.bottom = "0";
    root.style.right = "0";
    root.id = overlayRootId;
    document.body.appendChild(root);
  }

  return root;
}

function readjustRootOverlay(newChatOpenState: boolean) {
  const root = document.getElementById(overlayRootId);

  if (!root) {
    return;
  }

  if (newChatOpenState) {
    root.style.bottom = "24px";
    root.style.right = "24px";
  } else {
    root.style.bottom = "0";
    root.style.right = "0";
  }
}
